import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/** Convert a URL-safe base64 VAPID public key to the Uint8Array PushManager needs. */
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) arr[i] = rawData.charCodeAt(i);
  return arr;
};

const subToJSON = (sub) => {
  const raw = sub.toJSON();
  return {
    endpoint: raw.endpoint,
    keys: {
      p256dh: raw.keys?.p256dh || "",
      auth: raw.keys?.auth || "",
    },
    ua: navigator.userAgent?.slice(0, 240) || null,
  };
};

/**
 * useWebPush — one hook covers the full lifecycle for the admin push toggle.
 *
 * States exposed:
 *   supported          — browser can subscribe at all
 *   permission         — 'default' | 'granted' | 'denied'
 *   subscribed         — server confirms an active subscription for this device
 *   status.count       — how many devices this admin has subscribed
 *   quietHours         — { start, end, tz, in_quiet_hours_now }
 *   busy               — in-flight subscribe/unsubscribe/test call
 *   error              — human-readable last-error string, or null
 *
 * Actions:
 *   enable()   — requests permission + subscribes + posts to server
 *   disable()  — unsubscribes locally + tells server to prune
 *   sendTest() — hits POST /api/admin/push/test
 */
export default function useWebPush() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/push/status`, {
        headers: authHeaders(),
      });
      setStatus(data);
      // Cross-check with the local PushManager — the server-side count can
      // include OTHER devices, we only care whether THIS device is subscribed.
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager.getSubscription();
        setSubscribed(!!sub);
      }
    } catch (e) {
      // 401 during dev = not logged in; leave state untouched.
      if (e?.response?.status !== 401) {
        console.warn("[useWebPush] status refresh failed:", e?.message || e);
      }
    }
  }, []);

  useEffect(() => {
    const ok =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    refreshStatus();
  }, [refreshStatus]);

  const enable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (!supported) throw new Error("This browser doesn't support web push.");
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error(
          perm === "denied"
            ? "Notifications are blocked. Enable them from your browser or system settings."
            : "Notification permission was dismissed.",
        );
      }

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { data } = await axios.get(`${API}/push/public-key`);
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
      }
      await axios.post(`${API}/admin/push/subscribe`, subToJSON(sub), {
        headers: authHeaders(),
      });
      setSubscribed(true);
      await refreshStatus();
      return true;
    } catch (e) {
      setError(e?.message || "Could not enable notifications.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported, refreshStatus]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        try {
          await axios.delete(`${API}/admin/push/subscribe`, {
            headers: authHeaders(),
            params: { endpoint },
          });
        } catch (err) {
          // server-side gone is fine, local unsubscribe is the source of truth
          console.warn("[useWebPush] server unsubscribe failed (continuing with local):", err?.message || err);
        }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      await refreshStatus();
    } catch (e) {
      setError(e?.message || "Could not disable notifications.");
    } finally {
      setBusy(false);
    }
  }, [refreshStatus]);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/push/test`,
        {},
        { headers: authHeaders() },
      );
      return data;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Test send failed.");
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    supported,
    permission,
    subscribed,
    status,
    busy,
    error,
    enable,
    disable,
    sendTest,
    refreshStatus,
  };
}
